import {
    DialogSize,
    Icon,
    type IconTypes,
    Tooltip,
    useDialog,
} from '@ics-portal/guardian';
import { type ReactNode, useCallback, useMemo } from 'react';
import {
    type CellRendererParams,
    type TableActionsConfig,
    type TableMenuItem,
} from '@components/Table/types';
import { ComponentCreateRequestStatusCode } from '@gql/graphql';
import { ActionDialog } from '@pages/notification-components/components/RequestsCard/RequestsTable/components/ActionDialog';
import { getActionDialogName } from '@pages/notification-components/components/RequestsCard/RequestsTable/utils/getActionDialogName';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';

interface ActionMenuItemData {
    code: ComponentCreateRequestStatusCode;
    label: string;
    icon: ReactNode;
    size?: DialogSize;
    elementId: string;
}

function getSelectedRows(
    params: CellRendererParams<ComponentCreateRequestMapped>,
): ComponentCreateRequestMapped[] {
    return params.context.selectedRows ?? params.api.getSelectedRows() ?? [];
}

function actionIsDisabled(
    action: ComponentCreateRequestStatusCode,
    selectedRows: ComponentCreateRequestMapped[],
): boolean {
    return selectedRows.some(
        (row) =>
            row.statusTransitions.find(
                (transition) => transition.code === action,
            )?.blocked,
    );
}

function getBlockedTooltipContent(
    action: ComponentCreateRequestStatusCode,
    selectedRows: ComponentCreateRequestMapped[],
): ReactNode {
    const blockers = selectedRows.reduce<ReactNode[]>((acc, row) => {
        const transition = row.statusTransitions.find((t) => t.code === action);

        if (transition?.blocked) {
            const message = (
                <div className="flex flex-column gap-1">
                    Blocked by {row.componentName}:{' '}
                    <strong>{transition.reason ?? 'Unknown'}</strong>
                </div>
            );
            acc.push(message);
        }

        return acc;
    }, []);

    return (
        <div className="flex flex-column gap-2">
            {blockers.map((blocker, idx) => (
                <div key={idx}>{blocker}</div>
            ))}
        </div>
    );
}

function createActionMenuItemData(
    code: ComponentCreateRequestStatusCode,
    label: string,
    iconName: IconTypes,
    size?: DialogSize,
): ActionMenuItemData {
    const elementId = `ctx-action-${code}`.toLowerCase();

    return {
        code,
        label,
        icon: <Icon name={iconName} />,
        size,
        elementId,
    };
}

const ALL_ACTIONS_DATA: ActionMenuItemData[] = [
    createActionMenuItemData(
        ComponentCreateRequestStatusCode.Approved,
        'Approve',
        'check_circle',
    ),
    createActionMenuItemData(
        ComponentCreateRequestStatusCode.Forwarded,
        'Forward',
        'send_money',
    ),
    createActionMenuItemData(
        ComponentCreateRequestStatusCode.Replaced,
        'Replace',
        'published_with_changes',
        DialogSize.md,
    ),
    createActionMenuItemData(
        ComponentCreateRequestStatusCode.Removed,
        'Remove',
        'block',
    ),
];

export function useRequestTableActionsConfig(): TableActionsConfig<ComponentCreateRequestMapped> {
    const { form } = useDialog();

    const showActionModal = useCallback(
        (
            code: ComponentCreateRequestStatusCode,
            selectedRows: ComponentCreateRequestMapped[],
            size?: DialogSize,
        ): void => {
            const actionName = getActionDialogName(code);
            form({
                title: `${actionName} request`,
                content: (
                    <ActionDialog action={code} selectedRows={selectedRows} />
                ),
                size: size ? size : DialogSize.sm,
                buttons: {
                    submitLabel: actionName,
                },
            });
        },
        [form],
    );

    const customItems = useCallback(
        (
            params: CellRendererParams<ComponentCreateRequestMapped>,
        ): TableMenuItem<ComponentCreateRequestMapped>[] => {
            const selectedRows = getSelectedRows(params);

            if (selectedRows.length === 0) {
                return [];
            }

            return ALL_ACTIONS_DATA.filter((actionData) =>
                selectedRows.every((row) =>
                    row.statusTransitions.some(
                        (t) => t.code === actionData.code,
                    ),
                ),
            ).map((actionData): TableMenuItem<ComponentCreateRequestMapped> => {
                const disabled = actionIsDisabled(
                    actionData.code,
                    selectedRows,
                );
                const tooltipContent = getBlockedTooltipContent(
                    actionData.code,
                    selectedRows,
                );

                return {
                    label: actionData.label,
                    icon: actionData.icon,
                    onSelected: true,
                    disabled,
                    handler: (
                        handlerParams: CellRendererParams<ComponentCreateRequestMapped>,
                    ): void => {
                        const handlerSelectedRows =
                            getSelectedRows(handlerParams);
                        if (disabled || handlerSelectedRows.length === 0) {
                            return;
                        }
                        showActionModal(
                            actionData.code,
                            handlerSelectedRows,
                            actionData.size,
                        );
                    },
                    template: (item, options) => (
                        <>
                            {disabled ? (
                                <Tooltip
                                    target={`#${actionData.elementId}`}
                                    position="left"
                                    showOnDisabled
                                >
                                    {tooltipContent}
                                </Tooltip>
                            ) : null}
                            <a
                                id={actionData.elementId}
                                className={options.className}
                                onClick={options.onClick}
                                aria-disabled={disabled ? 'true' : undefined}
                            >
                                {item.icon}
                                {item.label}
                            </a>
                        </>
                    ),
                };
            });
        },
        [showActionModal],
    );

    return useMemo(
        (): TableActionsConfig<ComponentCreateRequestMapped> => ({
            customItems,
        }),
        [customItems],
    );
}
